import { PersonaCreateBlocksEntityRelationInput, PersonaGender, PersonaStatus } from '../../gql/graphql';
import { OOD_COLORS_LIST } from '../../types/colors';

export const quoteList: string[] = [
	'Dream big, work hard, stay focused.',
	'Life is short, smile while you still have teeth.',
	'Be kind, for everyone you meet is fighting a battle.',
	'Live with passion, love with purpose.',
	'Inhale courage, exhale fear.',
	'Happiness is an inside job.',
	'Strive for progress, not perfection.',
	'Embrace the glorious mess that you are.',
	'Create your own sunshine.',
	'Chase your dreams, not the competition.',
	'Your vibe attracts your tribe.',
	'Do more things that make you forget to check your phone.',
	"Life is what happens when you're busy making other plans.",
	'The best way to predict the future is to create it.',
	'In the middle of difficulty lies opportunity.',
	'Good things come to those who hustle.',
	'Stay curious and keep learning.',
	'The only way to do great work is to love what you do.',
	"Believe you can, and you're halfway there.",
	'Simplicity is the ultimate sophistication.',
];

export const descriptionList: string[] = [
	'Passionate soul with a love for adventure. Always seeking new challenges and finding joy in the journey. Living life one smile at a time.',
	'A creative spirit with a heart for kindness. Balancing work and play, I believe in the power of laughter and the beauty of simple moments.',
	'Eternal optimist and perpetual dreamer. Striving for a life filled with purpose, love, and a touch of wanderlust. Making memories that last a lifetime.',
	'A curious mind on a perpetual quest for knowledge. Embracing the art of learning and the thrill of exploration. In every moment, a new discovery awaits.',
	'Ambitious go-getter with a heart full of dreams. Believer in the extraordinary and the magic of hard work. Creating my own path to success.',
	"Nature enthusiast and outdoor adventurer. Finding solace in the beauty of the world around me. Living life by the mantra: 'Leave no stone unturned.'",
	"Free spirit and aspiring world-changer. Dedicated to spreading positivity and making a difference. Life is a canvas, and I'm painting it with love.",
	'Lover of stories and collector of memories. Cherishing the simple joys and finding magic in the everyday. Building a life that feels like a grand adventure.',
	"Tech geek with a passion for innovation. Constantly pushing boundaries and embracing the ever-evolving world of technology. Coding my way through life's challenges.",
	'Music lover and aspiring rockstar. Believer in the power of melodies and the magic of lyrics. Living each day with rhythm and soul.',
	'Adventurous soul with a heart for travel. Exploring new cultures, tasting exotic flavors, and collecting stories from around the globe. The world is my playground.',
	'Whimsical dreamer and hopeless romantic. Finding beauty in the details and creating a love story that transcends time. Every moment is a chapter in my fairy tale.',
	'Fitness fanatic and wellness warrior. Committed to a healthy lifestyle and spreading the joy of movement. Turning sweat into strength and goals into achievements.',
	'Coffee connoisseur and bookworm. Exploring the world one sip and one page at a time. Engaging in meaningful conversations and savoring the richness of life.',
	'Sustainability advocate and eco-warrior. Passionate about protecting our planet and creating a greener future. Every small action counts in the journey to sustainability.',
	"Artistic soul with a flair for expression. Transforming emotions into brushstrokes and words into poetry. Life is a canvas, and I'm painting it with passion.",
	"Aspiring chef and culinary explorer. Infusing flavors, experimenting with recipes, and creating gastronomic delights. Every dish tells a story, and I'm the storyteller.",
	'Strategist with a vision for success. Navigating the complexities of business and leadership. Turning challenges into opportunities and goals into achievements.',
	'Animal lover and advocate for furry friends. Creating a world where every paw print leaves a positive impact. Life is better with a loyal companion by your side.',
	'Fashion enthusiast with an eye for style. Curating outfits that tell a story and expressing creativity through fabrics and accessories. Every day is a fashion show.',
	'Mindful meditator and advocate for mental well-being. Nurturing a calm mind in the chaos of life. Finding peace in the present moment and spreading positivity.',
];

export const statusList: PersonaStatus[] = ['single', 'married', 'divorced', 'widow'];

export const genderList: PersonaGender[] = ['male', 'female'];

export const jobList: string[] = [
	'Web Developer',
	'Lawyer',
	'Graphic Designer',
	'Marketing Specialist',
	'Data Scientist',
	'Product Manager',
	'Teacher',
	'Chef',
	'Entrepreneur',
	'Nurse',
	'Architect',
	'Financial Analyst',
	'Social Media Manager',
	'Fitness Trainer',
	'UX/UI Designer',
	'Journalist',
	'Electrician',
	'Pharmacist',
	'Event Planner',
	'Psychologist',
];

export const locationList: string[] = [
	'Silicon Valley, CA',
	'New York, NY',
	'Los Angeles, CA',
	'London, UK',
	'Tokyo, Japan',
	'Paris, France',
	'Sydney, Australia',
	'Toronto, Canada',
	'Berlin, Germany',
	'Austin, TX',
	'Singapore',
	'Miami, FL',
	'Stockholm, Sweden',
	'Dubai, UAE',
	'Hong Kong',
	'Cape Town, South Africa',
	'Mumbai, India',
	'Seoul, South Korea',
	'Rio de Janeiro, Brazil',
	'Vancouver, Canada',
];

const personalityList: string[] = [
	'Calm',
	'Friendly',
	'Adventurous',
	'Optimistic',
	'Analytical',
	'Creative',
	'Ambitious',
	'Easygoing',
	'Inquisitive',
	'Empathetic',
];

const needsList: string[] = [
	'Stability',
	'Money',
	'Advice',
	'Independence',
	'Recognition',
	'Challenge',
	'Security',
	'Creativity',
	'Comfort',
	'Love',
];

const knowledgeList: string[] = [
	'PC skills',
	'Communication',
	'Creativity',
	'Marketing',
	'Data Analysis',
	'Problem Solving',
	'Project Management',
	'Foreign Languages',
	'Negotiation',
	'Public Speaking',
];

const likesList: string[] = ['Children', 'Pets', 'Reading', 'Traveling', 'Sports', 'Music', 'Art', 'Gardening', 'Movies', 'Cooking'];

const getRandomItems = (array: string[]) => {
	const randomItems: { create: { name: string; value: number } }[] = [];
	while (randomItems.length < 4) {
		const randomIndex = Math.floor(Math.random() * array.length);
		const randomTrait = array[randomIndex];
		if (!randomItems.some((item) => item.create.name === randomTrait)) {
			randomItems.push({ create: { name: randomTrait, value: Math.floor(Math.random() * 5) + 1 } });
		}
	}

	return randomItems;
};

const shuffleArray = (array: string[]) => {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
	return array;
};

export const getPersonaTemplateBlocks = (): PersonaCreateBlocksEntityRelationInput[] => {
	const shuffledColors = shuffleArray([...OOD_COLORS_LIST]);

	const personalityBlock = {
		create: {
			name: 'Personality',
			description:
				'Explore diverse personality traits, from calm and friendly to adventurous and creative, shaping the unique essence of each virtual profile.',
			color: shuffledColors.pop(),
			items: getRandomItems(personalityList),
			order: 0,
		},
	};
	const needsBlock = {
		create: {
			name: 'Needs',
			description:
				'Uncover individual needs ranging from stability and recognition to love and adventure, influencing the pursuit of happiness and fulfillment.',
			color: shuffledColors.pop(),
			items: getRandomItems(needsList),
			order: 1,
		},
	};
	const knowledgeBlock = {
		create: {
			name: 'Knowledge',
			description:
				'Discover a spectrum of skills, from technical prowess like PC skills and coding to softer skills like communication and creativity, defining each profile.',
			color: shuffledColors.pop(),
			items: getRandomItems(knowledgeList),
			order: 2,
		},
	};
	const likesBlock = {
		create: {
			name: 'Likes',
			description:
				'Explore the passions and interests that make each profile unique, from a love for children and pets to hobbies like reading, traveling, and more.',
			color: shuffledColors.pop(),
			items: getRandomItems(likesList),
			order: 3,
		},
	};

	return [personalityBlock, needsBlock, knowledgeBlock, likesBlock];
};
